import React, { useRef } from 'react';
import { TextField, Box } from '@mui/material';

/**
 * VariableHighlightInput - TextField with dynamic color feedback
 *
 * Features:
 * - ✅ {{variable}} -> green
 * - ❌ incomplete variables -> red
 * - normal text -> black
 * - No dangerouslySetInnerHTML
 */
const VariableHighlightInput = React.forwardRef((props, ref) => {
  const { value = '', onChange, multiline = false, rows, InputProps, ...otherProps } = props;
  const inputRef = useRef(null);

  // Determine color based on value
  const getTextColor = (text) => {
    if (!text) return '#4b5563'; // default black

    // Check for incomplete/malformed variables (unmatched braces)
    const openBraces = (text.match(/\{\{/g) || []).length;
    const closeBraces = (text.match(/\}\}/g) || []).length;

    // If braces are unmatched, show red (incomplete variable)
    if (openBraces !== closeBraces) return '#ef4444'; // ❌ incomplete

    // Check if text contains at least one complete variable {{...}}
    // This regex finds complete variables anywhere in the text
    const hasCompleteVariable = /\{\{[^{}]+\}\}/.test(text);

    // If entire text is just a variable, or contains valid variables, show green
    if (hasCompleteVariable) return '#22c55e'; // ✅ valid {{variable}} or text with variables

    // Plain text without variables
    return '#4b5563'; // normal
  };

  const inputColor = getTextColor(value);

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        {...otherProps}
        inputRef={ref || inputRef}
        value={value}
        onChange={onChange}
        multiline={multiline}
        rows={rows}
        InputProps={{
          ...InputProps,
          sx: {
            backgroundColor: 'transparent',
            '& input, & textarea': {
              color: inputColor + ' !important',  // ✅ Force color
              WebkitTextFillColor: inputColor + ' !important', // ✅ for Safari/Chrome
              fontWeight: 500,
            },
            ...InputProps?.sx,
          },
        }}
      />
    </Box>
  );
});

VariableHighlightInput.displayName = 'VariableHighlightInput';

export default VariableHighlightInput;
